import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  DescriptionList,
  Grid,
  Heading,
} from '@printdeal/ui-library';
import OrderDetailContext from '../../contexts/OrderDetailContext';
import { SanitizedLineItem } from '../../../../../types/order/Order';

import DeliverFiles from '../../shared/Actions/DeliverFiles/DeliverFiles';
import LineItemArtwork from '../LineItemArtwork/LineItemArtwork';
import LineItemFooter from '../LineItemFooter/LineItemFooter';

import canDeliverFilesHelper from '../../shared/Actions/DeliverFiles/CanDeliverFilesHelper';

import LineItemPrice from './LineItemPrice';
import LineItemState from '../../shared/Elements/LineItemState/LineItemState';
import LineItemDescription from '../LineItemDescription/LineItemDescription';
import SiblingLineItemsDescription from '../LineItemDescription/SiblingLineItemsDescription';
import DeliveryDateAndTime from '../DeliveryDateAndTime/DeliveryDateAndTime';
import LineItemPickupPoint from '../LineItemPickupPoint/LineItemPickupPoint';
import LineItemShipping from '../LineItemShipping/LineItemShipping';

interface LineItemCardProps {
  lineItem: SanitizedLineItem;
}

const LineItemCard = ({
  lineItem,
}: LineItemCardProps) => {
  const {
    custom: {
      fields: {
        lineItemNumber, reviewGroupSiblings,
      },
    },
    sane: {
      productName, lineItemQuantity, finalDeliveryDate, timedDeliveryInfo, isCancelled,
      hasPickupPoint,
    },
  } = lineItem;
  const { order } = useContext(OrderDetailContext);
  const canDeliverFiles = canDeliverFilesHelper(lineItem);

  if (!order) {
    return null;
  }

  const deliverFilesElement = canDeliverFiles.enabled
    && <DeliverFiles order={order} lineItem={lineItem} key="deliverFiles" />;

  return (
    <div className="pd-mb-4 pd-flex pd-flex-col pd-gap-3 pd-rounded pd-border pd-p-3 pd-text-sm" data-cy="orderLineItemCard">
      <div className="pd-flex pd-flex-wrap pd-justify-between pd-gap-4 md:pd-flex md:pd-flex-nowrap">
        <div className="pd-flex-none pd-shrink">
          {
            deliverFilesElement || <LineItemArtwork lineItem={lineItem} />
          }
        </div>
        <div className="pd-grow md:pd-order-2">
          <Grid className="pd-mb-3 pd-grid-cols-[1fr,auto] pd-gap-4">
            <Grid.Item>
              <Heading as="h3" className="pd-mb-2">
                {`${productName}, `}
                <FormattedMessage
                  id="account.order.orderline.quantity"
                  defaultMessage="{quantity} {quantity, plural, one {piece} other {pieces}}"
                  values={{ quantity: lineItemQuantity }}
                />
              </Heading>
              <LineItemState lineItem={lineItem} />
            </Grid.Item>
            <Grid.Item className="pd-text-right">
              <LineItemPrice lineItem={lineItem} />
            </Grid.Item>
          </Grid>
          <LineItemDescription lineItem={lineItem} />
          {
            reviewGroupSiblings && reviewGroupSiblings.length > 0 && (
              <SiblingLineItemsDescription reviewGroupSiblings={reviewGroupSiblings} />
            )
          }
          <hr className="pd-my-3" />
          <div className="pd-mb-3 pd-font-bold">
            <FormattedMessage
              id="account.order.delivery.title"
              defaultMessage="Delivery"
            />
          </div>
          {
            !isCancelled && (
              <DeliveryDateAndTime
                finalDeliveryDate={finalDeliveryDate}
                timedDeliveryInfo={timedDeliveryInfo}
              />
            )
          }
          {
            hasPickupPoint ? (
              <LineItemPickupPoint lineItem={lineItem} />
            ) : (
              <LineItemShipping lineItem={lineItem} />
            )
          }
          <DescriptionList className="pd-mt-2 md:pd-grid-cols-3">
            <DescriptionList.Title>
              <FormattedMessage
                id="account.order.lineitem.number"
                defaultMessage="Article number"
              />:
            </DescriptionList.Title>
            <DescriptionList.Content
              className="md:pd-col-span-2"
            >
              {lineItemNumber}
            </DescriptionList.Content>
          </DescriptionList>
        </div>
      </div>
      <LineItemFooter lineItem={lineItem} />
    </div>
  );
};

export default LineItemCard;
